import { useTranslation } from 'react-i18next'

export type FollowSource = {
  /** i18n key suffix — the entry reads `follow.<key>.name` and `follow.<key>.desc` */
  key: string
  /** i18n keys for the languages the source publishes in, listed beside the icons */
  langKeys: string[]
  links: { asset: string; labelKey: string; href: string }[]
}

export type FollowGroup = {
  /** i18n key for a line introducing this cluster; the first group usually needs none */
  labelKey?: string
  sources: FollowSource[]
}

interface Props {
  /** Any set of sources — the section owns none of its own */
  groups: FollowGroup[]
  /** i18n key for the line under the heading, naming what these sources cover */
  subKey: string
}

/**
 * Same three-line shape as the Inform list — name, short description — with a
 * row of social icons underneath, each linking to that account, and the
 * languages the source publishes in.
 */
export default function Follow({ groups, subKey }: Props) {
  const { t } = useTranslation()

  return (
    <div className='flex flex-col gap-6 w-full max-w-5xl'>
      <div className='flex flex-col gap-3'>
        <h2 className='text-2xl font-bold'>{t('follow.title')}</h2>
        <p className='opacity-70'>{t(subKey)}</p>
      </div>
      <div className='flex flex-col gap-8'>
        {groups.map(({ labelKey, sources }) => (
          <div key={labelKey ?? 'main'} className='flex flex-col gap-6'>
            {labelKey && <p className='font-semibold'>{t(labelKey)}</p>}
            {sources.map(({ key, langKeys, links }) => {
              const name = t(`follow.${key}.name`)
              const list = langKeys.map((langKey) => t(langKey)).join(t('follow.langSeparator'))
              return (
                <div key={key} className='flex flex-col gap-1'>
                  <strong>{name}</strong>
                  <p className='text-base opacity-70'>{t(`follow.${key}.desc`)}</p>
                  <div className='flex items-center gap-3 mt-1'>
                    {links.map(({ asset, labelKey: linkLabelKey, href }) => {
                      const label = t(linkLabelKey)
                      return (
                        <a
                          key={linkLabelKey}
                          href={href}
                          target='_blank'
                          rel='noopener noreferrer'
                          aria-label={`${name} — ${label}`}
                          className='opacity-100 hover:opacity-50 transition'
                        >
                          <img src={asset} alt={label} className='h-6 w-6' />
                        </a>
                      )
                    })}
                    <span className='text-sm opacity-60'>
                      {t(langKeys.length === 1 ? 'follow.language' : 'follow.languages', { list })}
                    </span>
                  </div>
                </div>
              )
            })}
          </div>
        ))}
      </div>
    </div>
  )
}
